/**
 * Testing Mode Context - Enables comprehensive system evaluation
 * Unlocks all lessons, provides debug controls, and enables progress manipulation
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';

interface PerformanceMetrics {
  loadTimes: Record<string, number>;
  apiCalls: number;
  errorCount: number;
  memoryUsage: number;
  renderCount: number;
  lastUpdated: string;
}

interface DebugInfo {
  currentRoute: string;
  userProgress: any;
  activeComponents: string[];
  apiStatus: Record<string, 'success' | 'error' | 'pending'>;
  lastError?: Error;
}

interface TestingModeConfig {
  unlockAllLessons: boolean;
  showDebugInfo: boolean;
  enableReset: boolean;
  skipProgressRequirements: boolean;
  showPerformanceMetrics: boolean;
  allowProgressManipulation: boolean;
  enableQuickNavigation: boolean;
  logAllActions: boolean;
}

interface TestingModeContextType {
  // Mode state
  isTestingMode: boolean;
  config: TestingModeConfig;
  
  // Debug information
  debugInfo: DebugInfo;
  performanceMetrics: PerformanceMetrics;
  actionLog: Array<{ timestamp: string; action: string; details: any }>;
  
  // Control actions
  enableTestingMode: () => void;
  disableTestingMode: () => void;
  updateConfig: (updates: Partial<TestingModeConfig>) => void;
  resetAllProgress: () => void;
  clearActionLog: () => void;
  
  // Navigation helpers
  jumpToLesson: (chapterId: string, lessonId: string) => void;
  unlockLesson: (lessonId: string) => void;
  setLessonProgress: (lessonId: string, progress: number) => void;
  
  // Debug utilities
  logAction: (action: string, details?: any) => void;
  updateDebugInfo: (updates: Partial<DebugInfo>) => void;
  recordPerformanceMetric: (key: string, value: number) => void;
  
  // Testing utilities
  exportTestingData: () => void;
  importTestingData: (data: any) => void;
  generateSystemSnapshot: () => any;
}

const TestingModeContext = createContext<TestingModeContextType | undefined>(undefined);

export const useTestingMode = () => {
  const context = useContext(TestingModeContext);
  if (!context) {
    throw new Error('useTestingMode must be used within a TestingModeProvider');
  }
  return context;
};

interface TestingModeProviderProps {
  children: React.ReactNode;
}

const defaultConfig: TestingModeConfig = {
  unlockAllLessons: true,
  showDebugInfo: true,
  enableReset: true,
  skipProgressRequirements: true,
  showPerformanceMetrics: true,
  allowProgressManipulation: true,
  enableQuickNavigation: true,
  logAllActions: true
};

const defaultDebugInfo: DebugInfo = {
  currentRoute: '',
  userProgress: null,
  activeComponents: [],
  apiStatus: {}
};

const defaultPerformanceMetrics: PerformanceMetrics = {
  loadTimes: {},
  apiCalls: 0,
  errorCount: 0,
  memoryUsage: 0,
  renderCount: 0,
  lastUpdated: new Date().toISOString()
};

export const TestingModeProvider: React.FC<TestingModeProviderProps> = ({ children }) => {
  // Check environment variable on initialization
  const [isTestingMode, setIsTestingMode] = useState(() => {
    return import.meta.env.REACT_APP_TESTING_MODE === 'true' || 
           import.meta.env.DEV ||
           localStorage.getItem('lyra-testing-mode') === 'true';
  });

  const [config, setConfig] = useState<TestingModeConfig>(() => {
    const saved = localStorage.getItem('lyra-testing-config');
    return saved ? { ...defaultConfig, ...JSON.parse(saved) } : defaultConfig;
  });

  const [debugInfo, setDebugInfo] = useState<DebugInfo>(defaultDebugInfo);
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics>(defaultPerformanceMetrics);
  const [actionLog, setActionLog] = useState<Array<{ timestamp: string; action: string; details: any }>>([]);

  // Save config to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('lyra-testing-config', JSON.stringify(config));
  }, [config]);

  // Save testing mode state
  useEffect(() => {
    localStorage.setItem('lyra-testing-mode', isTestingMode.toString());
  }, [isTestingMode]);

  // Performance monitoring
  useEffect(() => {
    if (!isTestingMode || !config.showPerformanceMetrics) return;

    const updateMetrics = () => {
      if ('memory' in performance && (performance as any).memory) {
        const memory = (performance as any).memory;
        recordPerformanceMetric('memoryUsage', memory.usedJSHeapSize);
      }
      
      setPerformanceMetrics(prev => ({
        ...prev,
        lastUpdated: new Date().toISOString()
      }));
    };

    const interval = setInterval(updateMetrics, 5000);
    return () => clearInterval(interval);
  }, [isTestingMode, config.showPerformanceMetrics]);

  // Mode controls
  const enableTestingMode = useCallback(() => {
    setIsTestingMode(true);
    logAction('Testing Mode Enabled');
    toast.success('Testing Mode Enabled - All lessons unlocked');
  }, []);

  const disableTestingMode = useCallback(() => {
    setIsTestingMode(false);
    logAction('Testing Mode Disabled');
    toast.info('Testing Mode Disabled - Normal progression restored');
  }, []);

  const updateConfig = useCallback((updates: Partial<TestingModeConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
    logAction('Config Updated', updates);
  }, []);

  const resetAllProgress = useCallback(() => {
    // Clear all progress from localStorage and services
    const progressKeys = Object.keys(localStorage).filter(key => 
      key.includes('progress') || key.includes('badge') || key.includes('completion')
    );
    
    progressKeys.forEach(key => localStorage.removeItem(key));
    
    // Reset action log
    setActionLog([]);
    
    // Notify components to refresh
    window.dispatchEvent(new CustomEvent('progress-reset'));
    
    logAction('All Progress Reset');
    toast.success('All progress has been reset');
  }, []);

  const clearActionLog = useCallback(() => {
    setActionLog([]);
    toast.info('Action log cleared');
  }, []);

  // Navigation helpers
  const jumpToLesson = useCallback((chapterId: string, lessonId: string) => {
    if (!isTestingMode) return;
    
    const path = `/chapter/${chapterId}/interactive/${lessonId}`;
    window.location.hash = path;
    window.location.href = path;
    
    logAction('Jump to Lesson', { chapterId, lessonId });
  }, [isTestingMode]);

  const unlockLesson = useCallback((lessonId: string) => {
    if (!isTestingMode) return;
    
    localStorage.setItem(`lesson-unlocked-${lessonId}`, 'true');
    logAction('Lesson Unlocked', { lessonId });
  }, [isTestingMode]);

  const setLessonProgress = useCallback((lessonId: string, progress: number) => {
    if (!isTestingMode || !config.allowProgressManipulation) return;
    
    localStorage.setItem(`lesson-progress-${lessonId}`, progress.toString());
    logAction('Progress Set', { lessonId, progress });
    
    // Notify components
    window.dispatchEvent(new CustomEvent('progress-updated', { 
      detail: { lessonId, progress } 
    }));
  }, [isTestingMode, config.allowProgressManipulation]);

  // Debug utilities
  const logAction = useCallback((action: string, details?: any) => {
    if (!config.logAllActions) return;
    
    const logEntry = {
      timestamp: new Date().toISOString(),
      action,
      details: details || {}
    };
    
    setActionLog(prev => [logEntry, ...prev.slice(0, 99)]); // Keep last 100 entries
    
    if (config.showDebugInfo) {
      console.log(`[Testing Mode] ${action}`, details);
    }
  }, [config.logAllActions, config.showDebugInfo]);

  const updateDebugInfo = useCallback((updates: Partial<DebugInfo>) => {
    setDebugInfo(prev => ({ ...prev, ...updates }));
  }, []);

  const recordPerformanceMetric = useCallback((key: string, value: number) => {
    setPerformanceMetrics(prev => ({
      ...prev,
      [key]: value,
      lastUpdated: new Date().toISOString()
    }));
  }, []);

  // Testing utilities
  const exportTestingData = useCallback(() => {
    const data = {
      config,
      debugInfo,
      performanceMetrics,
      actionLog: actionLog.slice(0, 50), // Export last 50 actions
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `lyra-testing-data-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    logAction('Testing Data Exported');
    toast.success('Testing data exported successfully');
  }, [config, debugInfo, performanceMetrics, actionLog]);

  const importTestingData = useCallback((data: any) => {
    try {
      if (data.config) setConfig({ ...defaultConfig, ...data.config });
      if (data.actionLog) setActionLog(data.actionLog);
      
      logAction('Testing Data Imported');
      toast.success('Testing data imported successfully');
    } catch (error) {
      toast.error('Failed to import testing data');
      logAction('Import Failed', error);
    }
  }, []);

  const generateSystemSnapshot = useCallback(() => {
    return {
      timestamp: new Date().toISOString(),
      route: window.location.pathname,
      localStorage: { ...localStorage },
      sessionStorage: { ...sessionStorage },
      config,
      debugInfo,
      performanceMetrics,
      actionLog: actionLog.slice(0, 10)
    };
  }, [config, debugInfo, performanceMetrics, actionLog]);

  // Update debug info based on route changes
  useEffect(() => {
    const updateRoute = () => {
      updateDebugInfo({ currentRoute: window.location.pathname });
    };
    
    updateRoute();
    window.addEventListener('popstate', updateRoute);
    
    return () => window.removeEventListener('popstate', updateRoute);
  }, [updateDebugInfo]);

  const value: TestingModeContextType = {
    // Mode state
    isTestingMode,
    config,
    
    // Debug information
    debugInfo,
    performanceMetrics,
    actionLog,
    
    // Control actions
    enableTestingMode,
    disableTestingMode,
    updateConfig,
    resetAllProgress,
    clearActionLog,
    
    // Navigation helpers
    jumpToLesson,
    unlockLesson,
    setLessonProgress,
    
    // Debug utilities
    logAction,
    updateDebugInfo,
    recordPerformanceMetric,
    
    // Testing utilities
    exportTestingData,
    importTestingData,
    generateSystemSnapshot
  };

  return (
    <TestingModeContext.Provider value={value}>
      {children}
    </TestingModeContext.Provider>
  );
};